/**
 * RailNav — the pharmacy navigation rail and its flyout cascade
 * (design/ux/01_foundations.md §6.1, §6.4; D-21, A-20; WP-18).
 *
 * Tree mode (PROPRIETAIRE / MANAGER): the 9 categories of NAV_TREE; level 2
 * and level 3 are flyout panels (portaled, positioned next to their parent).
 * Flat mode (CAISSIER, ADMIN_PHARMAKON): group headings and links, no flyout.
 *
 * Keyboard — APG vertical menubar, ONE Tab stop with a roving tabindex:
 *   Alt+M focus the rail on the current category · Alt+1…9 focus a category
 *   and open its panel · ↑/↓ Début/Fin, letters (type-ahead, accent-
 *   insensitive, 500 ms) · Entrée/Espace/→ open a panel on the item of the
 *   current path, else the first · ← / Échap close one level (Échap on the
 *   rail returns focus where it was before Alt+M) · Entrée on a page
 *   navigates and closes everything, Ctrl+Entrée opens a new tab · Tab is
 *   contained in an open panel stack, leaves the rail otherwise.
 *   The state machine is `cascadeKey` (lib/nav-tree.js, unit-tested).
 * Mouse — hover opens after 150 ms (preference « Ouvrir les menus au
 *   survol », on by default, OP-F-24), menu aim towards an open child panel,
 *   leaving the cascade closes it after 300 ms; a click toggles at once; pages
 *   are real links (Ctrl / middle click opens a tab). Click outside, window
 *   blur and route change close everything.
 * ARIA — nav « Navigation principale » › ul menubar (vertical); categories
 *   menuitem + aria-haspopup / aria-expanded / aria-controls; panels
 *   role="menu" labelled by their parent; pages <a role="menuitem" href>
 *   with aria-current="page" (ancestors "true"); a locked page says
 *   « module non souscrit » and stays focusable and clickable (the page's
 *   ModuleGate shows the offer, OP-F-10).
 */
import * as React from "react";
import { createPortal } from "react-dom";
import { useLocation, useNavigate } from "react-router-dom";
import { cn } from "@/lib/utils";
import { Icon, IconButton, Kbd, Wordmark } from "@/components/ui/primitives";
import { RoleBadge } from "@/components/domain/Domain";
import { useAuthStore } from "@/stores/authStore.js";
import { useThemeStore } from "@/stores/themeStore.js";
import { useNavStore } from "@/stores/navStore.js";
import { assetUrl } from "@/config/app.config.js";
import {
  NAV_HOVER_CLOSE_MS,
  NAV_HOVER_OPEN_MS,
  NAV_TYPEAHEAD_RESET_MS,
} from "@/config/ui.config.js";
import { useShortcuts } from "@/hooks/useShortcuts.js";
import { cascadeKey, inAimTriangle, moveIndex, typeAhead } from "@/lib/nav-tree.js";
import { useNavModel, type NavNode } from "./useNavModel";

type Focus = { level: number; index: number };
type Point = { x: number; y: number };

const isContainer = (n?: NavNode | null) => Array.isArray(n?.children);
const LOCKED_TEXT = "Module non souscrit";

/** Plain left click → client-side navigation; modified clicks stay native. */
function isPlainClick(e: React.MouseEvent) {
  return e.button === 0 && !e.ctrlKey && !e.metaKey && !e.shiftKey && !e.altKey;
}

/** Open a page in a new tab (Ctrl+Entrée), never with access to this window. */
function openInNewTab(to: string) {
  window.open(to, "_blank", "noopener");
}

// ── Pieces shared by the rail, the panels and the drawer ─────────────────────

export const NavBadge: React.FC<{ badgeKey?: string }> = ({ badgeKey }) => {
  const badge = useNavStore((s: any) => (badgeKey ? s.badges[badgeKey] : null));
  if (!badge) return null;
  return (
    <span className="pk-nav-badge">
      <span aria-hidden="true">{badge.count > 99 ? "99+" : badge.count}</span>
      <span className="sr-only">{` · ${badge.label}`}</span>
    </span>
  );
};

export const NavLock: React.FC<{ locked?: boolean }> = ({ locked }) =>
  locked ? (
    <>
      <Icon name="lock" size={14} className="pk-nav-lock" />
      <span className="sr-only"> · module non souscrit</span>
    </>
  ) : null;

// ── Rail frame (header, footer) ──────────────────────────────────────────────

const RailHeader: React.FC<{ compact: boolean }> = ({ compact }) => {
  const pharmacie = useAuthStore((s: any) => s.user?.pharmacie ?? null);
  const logoSrc = assetUrl(pharmacie?.logo_url);
  return (
    <div className="pk-rail-head">
      {logoSrc ? (
        <img src={logoSrc} alt="" className="pk-rail-logo" />
      ) : compact ? (
        <span className="pk-rail-monogram" aria-hidden="true">
          P
        </span>
      ) : null}
      {!compact && (
        <div className="pk-rail-brand">
          <Wordmark size="md" />
          {pharmacie?.nom && (
            <span className="pk-rail-pharmacy" title={pharmacie.nom}>
              {pharmacie.nom}
            </span>
          )}
        </div>
      )}
    </div>
  );
};

const RailFooter: React.FC<{ compact: boolean; canCollapse: boolean }> = ({ compact, canCollapse }) => {
  const role = useAuthStore((s: any) => s.user?.role ?? null);
  const collapsed = useThemeStore((s: any) => s.sidebarCollapsed);
  const toggleSidebar = useThemeStore((s: any) => s.toggleSidebar);
  return (
    <div className={cn("pk-rail-foot", compact && "pk-rail-foot--compact")}>
      {canCollapse && (
        <IconButton
          icon={collapsed ? "panel-left-open" : "panel-left-close"}
          label={collapsed ? "Étendre le menu" : "Réduire le menu"}
          aria-pressed={collapsed}
          onClick={toggleSidebar}
          className="pk-rail-toggle"
        />
      )}
      {!compact && role && (
        <span className="pk-rail-role">
          <RoleBadge role={role} size="sm" />
        </span>
      )}
    </div>
  );
};

// ── Hooks ────────────────────────────────────────────────────────────────────

/** Type-ahead buffer that clears after NAV_TYPEAHEAD_RESET_MS. */
function useTypeAheadBuffer() {
  const buf = React.useRef({ text: "", at: 0 });
  return React.useCallback((ch: string) => {
    const now = Date.now();
    buf.current.text = now - buf.current.at > NAV_TYPEAHEAD_RESET_MS ? ch : buf.current.text + ch;
    buf.current.at = now;
    return buf.current.text;
  }, []);
}

/** Alt+M: the element to give focus back to on Échap (§4.5). */
function rememberFocus(ref: React.MutableRefObject<HTMLElement | null>, inside: (el: Element | null) => boolean) {
  const el = document.activeElement as HTMLElement | null;
  if (el && el !== document.body && !inside(el)) ref.current = el;
}

function restoreFocus(ref: React.MutableRefObject<HTMLElement | null>) {
  const el = ref.current;
  ref.current = null;
  if (el && document.contains(el)) el.focus();
  else (document.getElementById("main") as HTMLElement | null)?.focus();
}

// ── Flyout panel ─────────────────────────────────────────────────────────────

interface PanelProps {
  level: 1 | 2;
  parent: NavNode;
  hotkey?: number;
  id: string;
  childPanelId: string;
  openChildId?: string;
  pos: { top: number; left: number } | null;
  panelRef: React.RefObject<HTMLDivElement | null>;
  itemRefs: React.MutableRefObject<(HTMLElement | null)[]>;
  activeIndex: number;
  currentLeafId?: string;
  onItemKeyDown: (e: React.KeyboardEvent, level: number, index: number) => void;
  onItemFocus: (level: number, index: number) => void;
  onItemPointerEnter: (level: number, node: NavNode) => void;
  onContainerClick: (level: number, node: NavNode, index: number) => void;
  onLeafClick: (e: React.MouseEvent, node: NavNode) => void;
  onPointerEnter: () => void;
  onPointerLeave: () => void;
}

const FlyoutPanel: React.FC<PanelProps> = ({
  level,
  parent,
  hotkey,
  id,
  childPanelId,
  openChildId,
  pos,
  panelRef,
  itemRefs,
  activeIndex,
  currentLeafId,
  onItemKeyDown,
  onItemFocus,
  onItemPointerEnter,
  onContainerClick,
  onLeafClick,
  onPointerEnter,
  onPointerLeave,
}) => {
  const items = parent.children ?? [];
  const title = parent.label;
  const descId = `${id}-locked`;
  itemRefs.current = [];
  return (
    <div
      ref={panelRef as React.RefObject<HTMLDivElement>}
      className="pk-flyout"
      data-level={level + 1}
      style={pos ? { top: pos.top, left: pos.left } : { visibility: "hidden", top: 0, left: 0 }}
      onPointerEnter={onPointerEnter}
      onPointerLeave={onPointerLeave}
    >
      <div className="pk-flyout-head" aria-hidden="true">
        <span className="pk-flyout-title">{title}</span>
        {hotkey != null && <Kbd>{`Alt+${hotkey}`}</Kbd>}
      </div>
      <span id={descId} hidden>
        {LOCKED_TEXT}
      </span>
      <ul role="menu" id={id} aria-label={title} className="pk-flyout-list">
        {items.map((node, i) => {
          const container = isContainer(node);
          const isOpen = container && openChildId === node.id;
          const current = Boolean(node.__current);
          const isPage = !container && node.id === currentLeafId;
          const common = {
            ref: (el: HTMLElement | null) => {
              itemRefs.current[i] = el;
            },
            role: "menuitem",
            tabIndex: i === activeIndex ? 0 : -1,
            "data-level": level,
            "data-index": i,
            className: cn(
              "pk-flyout-item",
              current && "is-current",
              isOpen && "is-open",
              node.locked && "is-locked",
            ),
            onKeyDown: (e: React.KeyboardEvent) => onItemKeyDown(e, level, i),
            onFocus: () => onItemFocus(level, i),
            onPointerEnter: () => onItemPointerEnter(level, node),
          };
          return (
            <React.Fragment key={node.id}>
              {node.separatorBefore && i > 0 && <li role="separator" className="pk-flyout-sep" />}
              <li role="none">
                {container ? (
                  <button
                    type="button"
                    {...(common as any)}
                    aria-haspopup="menu"
                    aria-expanded={isOpen}
                    aria-controls={isOpen ? childPanelId : undefined}
                    aria-current={current ? "true" : undefined}
                    onClick={() => onContainerClick(level, node, i)}
                  >
                    <span className="pk-flyout-dot" aria-hidden="true" />
                    <span className="pk-flyout-label">{node.label}</span>
                    <NavBadge badgeKey={node.badge} />
                    <Icon name="chevron-right" size={12} className="pk-flyout-chevron" />
                  </button>
                ) : (
                  <a
                    {...(common as any)}
                    href={node.to}
                    aria-current={isPage ? "page" : undefined}
                    aria-describedby={node.locked ? descId : undefined}
                    onClick={(e) => onLeafClick(e, node)}
                  >
                    <span className="pk-flyout-dot" aria-hidden="true" />
                    <span className="pk-flyout-label">{node.label}</span>
                    <NavLock locked={node.locked} />
                    <NavBadge badgeKey={node.badge} />
                  </a>
                )}
              </li>
            </React.Fragment>
          );
        })}
      </ul>
    </div>
  );
};

// ── Tree rail ────────────────────────────────────────────────────────────────

const PANEL_MARGIN = 8;

function placePanel(anchor: DOMRect, left: number, panel: HTMLElement | null) {
  const h = panel?.offsetHeight ?? 0;
  const maxTop = window.innerHeight - h - PANEL_MARGIN;
  return { left, top: Math.max(PANEL_MARGIN, Math.min(anchor.top, maxTop)) };
}

const TreeRail: React.FC<{ compact: boolean }> = ({ compact }) => {
  const model = useNavModel();
  const rail = model.nodes;
  const navigate = useNavigate();
  const location = useLocation();
  const hoverOn = useThemeStore((s: any) => s.navHoverOpen !== false);
  const flyoutRequest = useNavStore((s: any) => s.flyoutRequest);
  const baseId = React.useId().replace(/:/g, "");

  const currentRail = Math.max(
    rail.findIndex((c) => c.__current),
    0,
  );
  const [open, setOpen] = React.useState<string[]>([]);
  const [focus, setFocus] = React.useState<Focus>({ level: 0, index: currentRail });
  const [rovingRail, setRovingRail] = React.useState(currentRail);
  // Commits left to move focus onto the state's item (0 = none). A panel is
  // measured before it is shown (visibility: hidden for one commit), so the
  // focus is retried on the next commits — never more than a few, so a later
  // re-render can never pull focus back into the rail.
  const moveFocus = React.useRef(0);
  const returnFocus = React.useRef<HTMLElement | null>(null);
  const pushType = useTypeAheadBuffer();

  const railRef = React.useRef<HTMLUListElement>(null);
  const railItems = React.useRef<(HTMLElement | null)[]>([]);
  const l2Ref = React.useRef<HTMLDivElement>(null);
  const l3Ref = React.useRef<HTMLDivElement>(null);
  const l2Items = React.useRef<(HTMLElement | null)[]>([]);
  const l3Items = React.useRef<(HTMLElement | null)[]>([]);
  const [pos2, setPos2] = React.useState<{ top: number; left: number } | null>(null);
  const [pos3, setPos3] = React.useState<{ top: number; left: number } | null>(null);

  const l1 = open[0] ? rail.find((c) => c.id === open[0]) ?? null : null;
  const l2 = l1 && open[1] ? (l1.children ?? []).find((c) => c.id === open[1]) ?? null : null;
  const panelId = (id: string) => `${baseId}-panel-${id.replace(/\W/g, "-")}`;

  const inside = React.useCallback(
    (el: Element | null) =>
      Boolean(
        el &&
          (railRef.current?.contains(el) || l2Ref.current?.contains(el) || l3Ref.current?.contains(el)),
      ),
    [],
  );

  // Keep the roving stop on the current category when the location changes.
  React.useEffect(() => {
    setRovingRail(currentRail);
  }, [currentRail]);

  // ── open / close ──────────────────────────────────────────────────────────
  const openTimer = React.useRef<number | null>(null);
  const closeTimer = React.useRef<number | null>(null);
  const clearTimers = () => {
    if (openTimer.current) window.clearTimeout(openTimer.current);
    if (closeTimer.current) window.clearTimeout(closeTimer.current);
    openTimer.current = null;
    closeTimer.current = null;
  };
  const closeAll = React.useCallback(() => {
    clearTimers();
    setOpen([]);
  }, []);

  React.useEffect(() => closeAll(), [location.pathname, closeAll]);
  React.useEffect(() => () => clearTimers(), []);

  React.useEffect(() => {
    if (open.length === 0) return undefined;
    const onDown = (e: PointerEvent) => {
      if (!inside(e.target as Element)) closeAll();
    };
    const onBlur = () => closeAll();
    document.addEventListener("pointerdown", onDown, true);
    window.addEventListener("blur", onBlur);
    return () => {
      document.removeEventListener("pointerdown", onDown, true);
      window.removeEventListener("blur", onBlur);
    };
  }, [open.length, inside, closeAll]);

  // ── panel placement (before paint: no flicker) ────────────────────────────
  React.useLayoutEffect(() => {
    if (!l1) {
      setPos2(null);
      return;
    }
    const i = rail.findIndex((c) => c.id === l1.id);
    const anchor = railItems.current[i]?.getBoundingClientRect();
    const railRect = railRef.current?.closest("aside")?.getBoundingClientRect();
    if (!anchor || !railRect) return;
    setPos2(placePanel(anchor, railRect.right, l2Ref.current));
  }, [l1, rail]);

  React.useLayoutEffect(() => {
    if (!l1 || !l2) {
      setPos3(null);
      return;
    }
    const i = (l1.children ?? []).findIndex((c) => c.id === l2.id);
    const anchor = l2Items.current[i]?.getBoundingClientRect();
    const p2 = l2Ref.current?.getBoundingClientRect();
    if (!anchor || !p2) return;
    setPos3(placePanel(anchor, p2.right - 1, l3Ref.current));
  }, [l1, l2, pos2]);

  // ── keyboard focus follows the state ──────────────────────────────────────
  React.useEffect(() => {
    if (moveFocus.current <= 0) return;
    moveFocus.current -= 1;
    const list = focus.level === 0 ? railItems : focus.level === 1 ? l2Items : l3Items;
    const el = list.current[focus.index];
    if (!el) return;
    el.focus();
    if (document.activeElement === el) moveFocus.current = 0;
  });

  const apply = (next: { open: string[]; focus: Focus }, kbd = true) => {
    setOpen(next.open);
    setFocus(next.focus);
    if (next.focus.level === 0) setRovingRail(next.focus.index);
    moveFocus.current = kbd ? 3 : 0;
  };

  const go = (to: string, newTab = false) => {
    closeAll();
    if (newTab) openInNewTab(to);
    else navigate(to);
  };

  // ── keys ──────────────────────────────────────────────────────────────────
  const onItemKeyDown = (e: React.KeyboardEvent, level: number, index: number) => {
    if (e.altKey || e.metaKey || (e.ctrlKey && e.key !== "Enter")) return;
    const items = level === 0 ? rail : level === 1 ? l1?.children ?? [] : l2?.children ?? [];
    if (e.key.length === 1 && e.key !== " ") {
      const i = typeAhead(
        items.map((n) => (level === 0 && compact ? n.shortLabel ?? n.label : n.label)),
        pushType(e.key),
        index,
      );
      e.preventDefault();
      if (i >= 0) {
        const nextOpen = level === 1 ? open.slice(0, 1) : open;
        const node = items[i];
        apply({
          open: level === 0 ? (open.length && isContainer(node) ? [node.id] : []) : nextOpen,
          focus: { level, index: i },
        });
      }
      return;
    }
    const r = cascadeKey(rail, { open, focus: { level, index } }, e);
    if (!r.handled) {
      // Maj+Tab leaving the rail closes its panel; the browser moves focus.
      if (r.state.open.length !== open.length) apply(r.state, false);
      return;
    }
    e.preventDefault();
    e.stopPropagation();
    const action = r.action as { type: string; to?: string; newTab?: boolean };
    if (action.type === "navigate" && action.to) {
      go(action.to, action.newTab);
      return;
    }
    if (action.type === "exit") {
      restoreFocus(returnFocus);
      return;
    }
    apply(r.state);
  };

  const onItemFocus = (level: number, index: number) => {
    setFocus({ level, index });
    if (level === 0) setRovingRail(index);
  };

  // ── Alt+M, Alt+1…9, breadcrumb category crumb ─────────────────────────────
  const focusMenu = React.useCallback(() => {
    rememberFocus(returnFocus, inside);
    apply({ open: [], focus: { level: 0, index: currentRail } });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentRail, inside]);

  const openCategory = React.useCallback(
    (index: number, focusInside: boolean) => {
      const node = rail[index];
      if (!node) return false;
      rememberFocus(returnFocus, inside);
      if (!isContainer(node)) {
        apply({ open: [], focus: { level: 0, index } });
        return true;
      }
      if (focusInside) {
        const cur = (node.children ?? []).findIndex((c) => c.__current);
        apply({ open: [node.id], focus: { level: 1, index: Math.max(cur, 0) } });
      } else apply({ open: [node.id], focus: { level: 0, index } });
      return true;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rail, inside],
  );

  useShortcuts("GLOBAL", {
    MENU: focusMenu,
    CATEGORIE: (_e: KeyboardEvent, ctx: { index: number }) => {
      // Alt+n is the category's fixed number (« Achats · Alt+2 »), not its
      // position among the categories a role sees.
      const i = rail.findIndex((c) => (c as any).hotkey === ctx.index + 1);
      return i >= 0 ? openCategory(i, false) : false;
    },
  });

  React.useEffect(() => {
    if (!flyoutRequest) return;
    const i = rail.findIndex((c) => c.id === flyoutRequest.categoryId);
    if (i >= 0) openCategory(i, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flyoutRequest?.seq]);

  // ── mouse: hover with menu aim ────────────────────────────────────────────
  const lastPoint = React.useRef<Point | null>(null);
  const onPointerMove = (e: React.PointerEvent) => {
    lastPoint.current = { x: e.clientX, y: e.clientY };
  };
  const scheduleOpen = (level: number, node: NavNode) => {
    if (!hoverOn) return;
    if (openTimer.current) window.clearTimeout(openTimer.current);
    const from = lastPoint.current;
    const childRect = (level === 0 ? l2Ref.current : l3Ref.current)?.getBoundingClientRect();
    const fire = (origin: Point | null) => {
      const now = lastPoint.current;
      const moved = origin && now && (origin.x !== now.x || origin.y !== now.y);
      // Still travelling towards the open child panel: siblings do not switch.
      if (childRect && moved && inAimTriangle(origin, now, childRect)) {
        openTimer.current = window.setTimeout(() => fire(now), NAV_HOVER_OPEN_MS);
        return;
      }
      openTimer.current = null;
      if (level === 0) setOpen(isContainer(node) ? [node.id] : []);
      else setOpen(isContainer(node) ? [open[0], node.id] : open.slice(0, 1));
    };
    openTimer.current = window.setTimeout(() => fire(from), NAV_HOVER_OPEN_MS);
  };
  const cancelClose = () => {
    if (closeTimer.current) window.clearTimeout(closeTimer.current);
    closeTimer.current = null;
  };
  const scheduleClose = () => {
    if (!hoverOn) return;
    if (inside(document.activeElement)) return; // a keyboard user is in there
    cancelClose();
    closeTimer.current = window.setTimeout(() => {
      if (openTimer.current) window.clearTimeout(openTimer.current);
      setOpen([]);
    }, NAV_HOVER_CLOSE_MS);
  };

  const onLeafClick = (e: React.MouseEvent, node: NavNode) => {
    if (!node.to || !isPlainClick(e)) return;
    e.preventDefault();
    go(node.to);
  };
  const onCategoryClick = (node: NavNode, index: number) => {
    clearTimers();
    setRovingRail(index);
    setFocus({ level: 0, index });
    setOpen(open[0] === node.id ? [] : [node.id]);
  };
  const onContainerClick = (_level: number, node: NavNode) => {
    clearTimers();
    setOpen(open[1] === node.id ? open.slice(0, 1) : [open[0], node.id]);
  };

  const hotkeyOf = (n: NavNode) => (n as any).hotkey as number | undefined;

  return (
    <>
      <nav
        aria-label="Navigation principale"
        className="pk-rail-nav"
        onPointerMove={onPointerMove}
        onPointerEnter={cancelClose}
        onPointerLeave={scheduleClose}
      >
        <ul ref={railRef} role="menubar" aria-orientation="vertical" aria-label="Navigation principale" className="pk-rail-list">
          {rail.map((node, i) => {
            const container = isContainer(node);
            const isOpen = open[0] === node.id;
            const current = Boolean(node.__current);
            const label = compact ? node.shortLabel ?? node.label : node.label;
            const common = {
              ref: (el: HTMLElement | null) => {
                railItems.current[i] = el;
              },
              role: "menuitem",
              tabIndex: i === rovingRail ? 0 : -1,
              "data-level": 0,
              "data-index": i,
              "aria-label": compact && label !== node.label ? node.label : undefined,
              className: cn("pk-rail-item", current && "is-current", isOpen && "is-open"),
              onKeyDown: (e: React.KeyboardEvent) => onItemKeyDown(e, 0, i),
              onFocus: () => onItemFocus(0, i),
              onPointerEnter: () => scheduleOpen(0, node),
            };
            return (
              <li role="none" key={node.id}>
                {container ? (
                  <button
                    type="button"
                    {...(common as any)}
                    aria-haspopup="menu"
                    aria-expanded={isOpen}
                    aria-controls={isOpen ? panelId(node.id) : undefined}
                    aria-current={current ? "true" : undefined}
                    onClick={() => onCategoryClick(node, i)}
                  >
                    <Icon name={node.icon ?? ""} size={22} className="pk-rail-icon" />
                    <span className="pk-rail-label">{label}</span>
                    {!compact && <Icon name="chevron-right" size={16} className="pk-rail-chevron" />}
                  </button>
                ) : (
                  <a
                    {...(common as any)}
                    href={node.to}
                    aria-current={current ? "page" : undefined}
                    onClick={(e) => onLeafClick(e, node)}
                  >
                    <Icon name={node.icon ?? ""} size={22} className="pk-rail-icon" />
                    <span className="pk-rail-label">{label}</span>
                  </a>
                )}
              </li>
            );
          })}
        </ul>
      </nav>
      {l1 &&
        typeof document !== "undefined" &&
        createPortal(
          <FlyoutPanel
            level={1}
            parent={l1}
            hotkey={hotkeyOf(l1)}
            id={panelId(l1.id)}
            childPanelId={l2 ? panelId(l2.id) : ""}
            openChildId={l2?.id}
            pos={pos2}
            panelRef={l2Ref}
            itemRefs={l2Items}
            activeIndex={focus.level === 1 ? focus.index : -1}
            currentLeafId={model.active?.leaf.id}
            onItemKeyDown={onItemKeyDown}
            onItemFocus={onItemFocus}
            onItemPointerEnter={(level, node) => scheduleOpen(level, node)}
            onContainerClick={onContainerClick}
            onLeafClick={onLeafClick}
            onPointerEnter={cancelClose}
            onPointerLeave={scheduleClose}
          />,
          document.body,
        )}
      {l1 &&
        l2 &&
        typeof document !== "undefined" &&
        createPortal(
          <FlyoutPanel
            level={2}
            parent={l2}
            id={panelId(l2.id)}
            childPanelId=""
            pos={pos3}
            panelRef={l3Ref}
            itemRefs={l3Items}
            activeIndex={focus.level === 2 ? focus.index : -1}
            currentLeafId={model.active?.leaf.id}
            onItemKeyDown={onItemKeyDown}
            onItemFocus={onItemFocus}
            onItemPointerEnter={() => cancelClose()}
            onContainerClick={onContainerClick}
            onLeafClick={onLeafClick}
            onPointerEnter={cancelClose}
            onPointerLeave={scheduleClose}
          />,
          document.body,
        )}
    </>
  );
};

// ── Flat rail (CAISSIER, ADMIN_PHARMAKON) ────────────────────────────────────

const FlatRail: React.FC<{ compact: boolean }> = ({ compact }) => {
  const model = useNavModel();
  const navigate = useNavigate();
  const groups = model.nodes;
  const flat = React.useMemo(() => groups.flatMap((g) => g.children ?? []), [groups]);
  const currentIndex = Math.max(
    flat.findIndex((n) => n.id === model.active?.leaf.id),
    0,
  );
  const [roving, setRoving] = React.useState(currentIndex);
  React.useEffect(() => setRoving(currentIndex), [currentIndex]);
  const refs = React.useRef<(HTMLElement | null)[]>([]);
  const returnFocus = React.useRef<HTMLElement | null>(null);
  const listRef = React.useRef<HTMLUListElement>(null);
  const pushType = useTypeAheadBuffer();
  const inside = React.useCallback((el: Element | null) => Boolean(el && listRef.current?.contains(el)), []);

  const focusAt = (i: number) => {
    setRoving(i);
    refs.current[i]?.focus();
  };
  useShortcuts("GLOBAL", {
    MENU: () => {
      rememberFocus(returnFocus, inside);
      focusAt(currentIndex);
    },
  });

  const onKeyDown = (e: React.KeyboardEvent, i: number) => {
    if (e.altKey || e.metaKey) return;
    const node = flat[i];
    if (e.key === "Enter" && e.ctrlKey && node?.to) {
      e.preventDefault();
      openInNewTab(node.to);
      return;
    }
    if (e.ctrlKey) return;
    if (["ArrowDown", "ArrowUp", "Home", "End"].includes(e.key)) {
      e.preventDefault();
      focusAt(moveIndex(flat.length, i, e.key));
      return;
    }
    if (e.key === "Escape") {
      e.preventDefault();
      restoreFocus(returnFocus);
      return;
    }
    if (e.key === " " && node?.to) {
      e.preventDefault();
      navigate(node.to);
      return;
    }
    if (e.key.length === 1 && e.key !== " ") {
      e.preventDefault();
      const j = typeAhead(
        flat.map((n) => n.label),
        pushType(e.key),
        i,
      );
      if (j >= 0) focusAt(j);
    }
  };

  let k = -1;
  return (
    <nav aria-label="Navigation principale" className="pk-rail-nav">
      <ul ref={listRef} role="menubar" aria-orientation="vertical" aria-label="Navigation principale" className="pk-rail-list pk-rail-list--flat">
        {groups.map((g) => {
          const headingId = `nav-group-${g.id.replace(/\W/g, "-")}`;
          return (
            <li role="none" key={g.id} className="pk-rail-group">
              {!compact && (
                <div id={headingId} className="pk-rail-group-heading">
                  {g.label}
                </div>
              )}
              <ul role="group" aria-labelledby={compact ? undefined : headingId} aria-label={compact ? g.label : undefined}>
                {(g.children ?? []).map((node) => {
                  k += 1;
                  const i = k;
                  const isPage = node.id === model.active?.leaf.id;
                  return (
                    <li role="none" key={node.id}>
                      <a
                        ref={(el) => {
                          refs.current[i] = el;
                        }}
                        role="menuitem"
                        href={node.to}
                        tabIndex={i === roving ? 0 : -1}
                        aria-current={isPage ? "page" : undefined}
                        className={cn("pk-rail-item", isPage && "is-current")}
                        onKeyDown={(e) => onKeyDown(e, i)}
                        onFocus={() => setRoving(i)}
                        onClick={(e) => {
                          if (!node.to || !isPlainClick(e)) return;
                          e.preventDefault();
                          navigate(node.to);
                        }}
                      >
                        <Icon name={node.icon ?? ""} size={22} className="pk-rail-icon" />
                        <span className="pk-rail-label">{node.label}</span>
                        <NavBadge badgeKey={node.badge} />
                      </a>
                    </li>
                  );
                })}
              </ul>
            </li>
          );
        })}
      </ul>
    </nav>
  );
};

// ── The rail ─────────────────────────────────────────────────────────────────

export interface RailNavProps {
  /** compact = 88 px (1024–1279 px, or collapsed by the user). */
  compact: boolean;
  /** The footer toggle is offered only where the user's choice applies (≥ 1280 px). */
  canCollapse: boolean;
}

export const RailNav: React.FC<RailNavProps> = ({ compact, canCollapse }) => {
  const mode = useNavModel().mode;
  return (
    <>
      <RailHeader compact={compact} />
      {mode === "tree" ? <TreeRail compact={compact} /> : mode === "flat" ? <FlatRail compact={compact} /> : <div className="pk-rail-nav" />}
      <RailFooter compact={compact} canCollapse={canCollapse} />
    </>
  );
};

export default RailNav;
